"use client";

import { useCallback, useState } from "react";
import { signIn } from "next-auth/react";

import { useLanguage } from "@/contexts/LanguageContext";
import { getStrings } from "@/i18n/dictionaries";
import { getCurrentSafeAuthCallbackUrl, getSafeAuthCallbackUrl } from "@/lib/auth/safeCallbackUrl";

export default function DirectGoogleLoginButton({
  children,
  className = "",
  containerClassName = "",
  callbackUrl,
  onStart,
}) {
  const { locale } = useLanguage();
  const t = getStrings(locale, "auth");
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = useCallback(async () => {
    if (isConnecting) return;

    setIsConnecting(true);
    setError("");
    onStart?.();

    try {
      const safeCallback = callbackUrl
        ? getSafeAuthCallbackUrl(callbackUrl)
        : getCurrentSafeAuthCallbackUrl();
      const response = await signIn("google", {
        callbackUrl: safeCallback,
        redirect: false,
      });

      if (!response || response.error || !response.url) {
        throw new Error(response?.error || "Google sign-in could not be started.");
      }

      window.location.assign(response.url);
    } catch (loginError) {
      console.error("[auth] Google sign-in failed", loginError);
      setError(t.login_retry_error);
      setIsConnecting(false);
    }
  }, [callbackUrl, isConnecting, onStart, t.login_retry_error]);

  return (
    <div className={`relative ${containerClassName}`}>
      <button
        type="button"
        onClick={handleLogin}
        disabled={isConnecting}
        aria-busy={isConnecting}
        className={className}
      >
        {isConnecting ? t.connecting : children}
      </button>
      {error ? (
        <p role="alert" className="absolute right-0 top-full z-10 mt-2 w-64 rounded-xl border border-rose-200 bg-white px-3 py-2 text-xs font-medium text-rose-700 shadow-lg">
          {error}
        </p>
      ) : null}
    </div>
  );
}
